// Cloud carbon connectors (AWS Sustainability, Carbon optimization in Azure, Google Cloud Carbon
// Footprint). Shapes mirror
// the backend's /cloud-emissions routes (controllers/cloudEmissions.controller.js).

export type CloudEmissionsProvider = 'aws-sustainability' | 'azure-carbon' | 'google-cloud-carbon';

export const CLOUD_EMISSIONS_PROVIDERS: CloudEmissionsProvider[] = ['aws-sustainability', 'azure-carbon', 'google-cloud-carbon'];

export const isCloudEmissionsProvider = (code?: string | null): code is CloudEmissionsProvider =>
  !!code && (CLOUD_EMISSIONS_PROVIDERS as string[]).includes(code);

/** Display names for tiles, headings and schedules. Vendor product names, never ours. */
export const CLOUD_PROVIDER_NAMES: Record<CloudEmissionsProvider, string> = {
  'aws-sustainability': 'AWS Sustainability',
  'azure-carbon': 'Carbon optimization in Azure',
  'google-cloud-carbon': 'Google Cloud Carbon Footprint',
};

/** What each vendor reports by: an AWS account, an Azure subscription, a Google Cloud project. */
export type CloudScopeLabel = 'account' | 'subscription' | 'project';

/** The scope noun for headings and sentences: "account", "Subscriptions". */
export const scopeNoun = (label: CloudScopeLabel, { plural = false, capitalised = false } = {}): string => {
  const word = plural ? `${label}s` : label;
  return capitalised ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word;
};

/** Vendors that report both Scope 2 bases, so the months table shows the other basis too. */
export const CLOUD_PROVIDERS_WITH_BOTH_BASES: CloudEmissionsProvider[] = ['aws-sustainability', 'google-cloud-carbon'];

/**
 * Wording that differs by vendor. Keyed by provider, so a new vendor cannot fall back to another
 * vendor's copy: TypeScript requires an entry for each.
 *   serviceBreakdownTitle  what the vendor's service breakdown is called
 *   bundledExample         another service a bundled invoice from this vendor typically carries
 *   weakSuppliersNote      why a supplier with only the parent company's name is not ticked
 *   scheduleTiming         when a month is published, for choosing a schedule day
 */
export const CLOUD_PROVIDER_COPY: Record<CloudEmissionsProvider, {
  serviceBreakdownTitle: string,
  bundledExample: string,
  weakSuppliersNote: string,
  scheduleTiming: string,
}> = {
  'aws-sustainability': {
    serviceBreakdownTitle: 'By service',
    bundledExample: 'Microsoft 365',
    weakSuppliersNote: 'a supplier named only "Amazon" or "Microsoft" is usually retail or licences, which the vendor report does not cover',
    scheduleTiming: 'AWS does not state how soon a month is published, so a monthly run late in the month is safest.',
  },
  'azure-carbon': {
    serviceBreakdownTitle: 'By resource type',
    bundledExample: 'Microsoft 365',
    weakSuppliersNote: 'a supplier named only "Amazon" or "Microsoft" is usually retail or licences, which the vendor report does not cover',
    scheduleTiming: 'Microsoft publishes the previous month by the 19th, so a monthly run on the 20th picks each month up as soon as it lands.',
  },
  'google-cloud-carbon': {
    serviceBreakdownTitle: 'By service',
    bundledExample: 'Google Workspace',
    weakSuppliersNote: 'a supplier named only "Google" is usually Google Workspace or advertising, which the Google Cloud report does not cover',
    scheduleTiming: 'Google exports each month on the 15th of the following month, so a monthly run on the 20th picks each month up as soon as it lands.',
  },
};

export type CloudLineStatus = 'READY' | 'REVIEW' | 'NEEDS_SETUP' | 'COMMITTED' | 'RESTATED' | 'LOCKED' | 'SKIPPED';
export type CloudMatchAction = 'ATTACH' | 'STANDALONE' | 'REVIEW' | 'NEEDS_SETUP' | 'LOCKED';
export type CloudReviewReason =
  | 'SEVERAL_CANDIDATES'
  | 'BUNDLED_SUPPLIER'
  | 'SUSPECTED_UNMAPPED_INVOICE'
  | 'MANUAL_MATCHING'
  | 'PAIRING_NOT_CONFIRMED';
export type CloudInvoiceMatching = 'MONTHLY' | 'MANUAL';
export type CloudBasis = 'MARKET' | 'LOCATION';

export interface ICloudScope {
  id: string;
  name: string;
  included: boolean;
  /** The vendor no longer lists it; kept so history still has a name. */
  missing?: boolean;
}

export interface ICloudMappedSupplier {
  id: string;
  /** Also bills services the vendor report does not cover (e.g. Microsoft 365). */
  bundled: boolean;
}

export interface ICloudSettings {
  supplierIds: ICloudMappedSupplier[];
  billingOffsetMonths: number;
  invoiceMatching: CloudInvoiceMatching;
  defaultCategoryId: string | null;
  supplierMappingConfirmedAt: string | null;
  pairingConfirmedAt: string | null;
  lastSync: {
    at: string;
    reportPeriodId: string;
    importId: string;
    source: string;
    newLines: number;
    restatedLines: number;
    breakdownStatus: string;
  } | null;
}

export interface ICloudSupplierSuggestion {
  id: string;
  name: string;
  strength: 'strong' | 'weak';
}

export interface ICloudConnectionStatus {
  provider: CloudEmissionsProvider;
  vendorName: string;
  scopeLabel: CloudScopeLabel;
  configured: boolean;
  connected: boolean;
  connection: {
    id: string;
    roleArn: string | null;
    accountId: string | null;
    /** Google Cloud: the Carbon Footprint table, "project.dataset.table". */
    bigQueryTable?: string | null;
    scopes: ICloudScope[];
    settings: ICloudSettings;
    mappedSuppliers: { id: string, name: string | null, bundled: boolean }[];
  } | null;
  setupGap: 'SUPPLIER_MAPPING' | 'DEFAULT_CATEGORY' | null;
  basis: CloudBasis;
  basisNote: string | null;
  exclusionsNote: string;
  defaultSyncDayOfMonth: number;
  suggestions: {
    suppliers: ICloudSupplierSuggestion[];
    defaultCategoryId: string | null;
  };
  aws: {
    principalArn: string | null;
    externalId: string | null;
    trustPolicy: Record<string, unknown> | null;
    permissionsPolicy: Record<string, unknown>;
    /** CloudFormation quick-create link; null until the role template is hosted. */
    launchUrl: string | null;
    /** The hosted template, so the admin can review it first. */
    templateUrl: string | null;
    /** Plain-text instructions for Copy instructions; null before setup has started. */
    instructionsText: string | null;
    lastInstructionsSent: ICloudInstructionsSent | null;
  } | null;
  gcp?: {
    /** The service account the customer's admin grants BigQuery Data Viewer. */
    serviceAccount: string | null;
    /** This connection's code: the value of the label on the customer's table. */
    code: string | null;
    labelKey: string;
    role: string;
    /** The connected table, "project.dataset.table". */
    table: string | null;
    /** Carbon Footprint in the Google Cloud console, where the export is turned on. */
    consoleUrl: string;
    /** Our Google Cloud customer id, for organisations that only allow their own domain. */
    customerId: string | null;
    /** Cloud Shell commands to grant access and add the label; PROJECT and DATASET until the table is known. */
    commands: { grant: string, label: string, usesPlaceholders: boolean } | null;
    instructionsText: string | null;
    lastInstructionsSent: ICloudInstructionsSent | null;
  } | null;
  /** Only on the connect response: how many accounts the role can see (0 is a working role). */
  connectResult?: { accountCount?: number, projectCount?: number };
}

export interface ICloudInstructionsSent {
  email: string;
  name: string | null;
  sentAt: string;
  sentByName: string | null;
}

export interface ICloudSendInstructions {
  recipientEmail: string;
  recipientName?: string;
  note?: string;
}

/** The longest message a user can add to the AWS instructions email. */
export const AWS_INSTRUCTIONS_NOTE_MAX = 500;

export interface ICloudBreakdownEntry {
  name: string;
  kg: number;
}

export interface ICloudLineDetail {
  scopeId: string;
  scopeName: string;
  monthKey: string;
  unit: 'kgCO2e';
  modelVersion: string | null;
  basis: CloudBasis;
  kgOnBasis: number;
  otherBasis: CloudBasis | null;
  kgOtherBasis: number | null;
  scopeSplit: {
    scope1Kg: number | null;
    scope2Kg: number | null;
    scope3Kg: number | null;
    scope2OtherBasisKg: number | null;
    scope3OtherBasisKg: number | null;
  };
  vendorBasis?: CloudBasis;
  basisMismatch: boolean;
  basisAssumption: string | null;
  exclusionsNote: string;
  breakdown: {
    byService: ICloudBreakdownEntry[];
    byRegion: ICloudBreakdownEntry[];
    status: 'OK' | 'UNAVAILABLE' | 'FAILED' | 'NOT_FETCHED';
    note: string | null;
  } | null;
  match?: {
    action: CloudMatchAction;
    reason: CloudReviewReason | 'SUPPLIER_MAPPING' | 'DEFAULT_CATEGORY' | null;
    emissionId: string | null;
    candidateEmissionIds: string[];
    suspectEmissionIds: string[];
    decidedAt: string;
    committedAt?: string;
    committedBy?: 'user' | 'auto';
  };
  restatement?: {
    bookedKg: number;
    bookedModelVersion: string | null;
    history: { kg: number, modelVersion: string | null, detectedAt: string }[];
    appliedAt: string | null;
    note?: string;
  } | null;
}

export interface ICloudLine {
  id: string;
  nativeId: string;
  date: string;
  /** kg CO2e on the organisation's reporting basis. */
  total: number;
  reference: string | null;
  reportPeriodId: string;
  isCommitted: boolean;
  carbonStatus: CloudLineStatus;
  carbonDetail: ICloudLineDetail;
}

export interface ICloudRecordSummary {
  id: string;
  startDate: string;
  endDate: string;
  supplierName: string | null;
  excludeFromReporting: boolean;
  locked: boolean;
  kgCounting: number;
  invoice: { amount: number, currencyCode: string | null, reference: string | null } | null;
  /** The ledger account the spend was posted to, when the record came from a ledger or spend import. */
  nominalCode?: string | null;
  nominalDescription?: string | null;
}

export interface ICloudLinesResponse {
  lines: ICloudLine[];
  records: Record<string, ICloudRecordSummary>;
}

export interface ICloudCoverageSummary {
  provider: CloudEmissionsProvider;
  fromKey: string | null;
  toKey: string | null;
  vendorEarliestKey: string | null;
  vendorLatestKey: string | null;
  scopesSynced: number;
  // Azure names a refused scope subscriptionId; other vendors scopeId.
  deniedScopes: { scopeId?: string, subscriptionId?: string, reason: string | null }[];
  monthsFound: string[];
  monthsMissing: { scopeId: string, monthKey: string }[];
  monthsNotYetPublished: string[];
  monthsBeforeVendorHistory: string[];
  newLines?: number;
  updatedLines?: number;
  restatedLines?: number;
  unchangedLines?: number;
  basis: CloudBasis;
  basisMismatch: boolean;
  breakdown: { status: string, note: string | null };
  statusCounts: Partial<Record<CloudLineStatus, number>>;
}

export interface ICloudSettingsUpdate {
  scopes?: { id: string, included: boolean }[];
  supplierIds?: ICloudMappedSupplier[];
  billingOffsetMonths?: number;
  invoiceMatching?: CloudInvoiceMatching;
  defaultCategoryId?: string | null;
  confirmSetup?: boolean;
  /** What the form offered, for the suggestion-outcome telemetry. Ids and enums only. */
  suggestion?: {
    suppliers: { id: string, strength: 'strong' | 'weak' }[];
    defaultCategoryId: string | null;
  };
}

export interface ICloudCommitResult {
  committed: { lineId: string, action: CloudMatchAction, emissionId: string | null }[];
  restated: { lineId: string, emissionId: string | null }[];
  skipped: { lineId: string, reason: string, message: string | null }[];
}

export type CloudResolveAction = 'attach' | 'standalone' | 'skip';
